import {
	IRestaurantsContext,
	RestaurantsContext,
} from 'modules/CustomersAndRestaurants/Restaurants/Restaurants/restaurantsState/restaurantsContext';
import React, { useContext, useEffect } from 'react';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { UserRoles } from 'types';
import { allRoles } from 'appConstants';
import { MainInfoTab } from './components/MainInfoTab/MainInfoTab';
import LocalizationTab from './components/LocalizationTab/LocalizationTab';
import StyleTab from './components/StyleTab/StyleTab';
import SoftwareTab from './components/SoftwareTab/SoftwareTab';
import DevicesTab from './components/DevicesTab/DevicesTab';
import AdditionallyTab from './components/AdditionallyTab/AdditionallyTab';
import WatchesTab from './components/WatchesTab/WatchesTab';
import ReportsTab from './components/ReportsTab/ReportsTab';
import BankSettingsTab from './components/BankSettingsTab/BankSettingsTab';
import { AndroidWatchesTab } from './components/AndroidWatchesTab/AndroidWatchesTab';
import {
	IRestaurantFullInfoContext,
	RestaurantFullInfoContext,
} from '../../../../restaurantFullInfoState/restaurantFullInfoContext';

const Tabs: React.FC = () => {
	const { fullInfoData } = useContext<IRestaurantsContext>(RestaurantsContext);
	const { changeRestaurantFullInfoVisibleForm, restaurantFullInfoVisibleForm } =
		useContext<IRestaurantFullInfoContext>(RestaurantFullInfoContext);
	const hasMainFormAccess = useRoleAccess(allRoles);
	const hasLocalizationFormAccess = useRoleAccess(allRoles);
	const hasStyleFormAccess = useRoleAccess(allRoles);
	const hasSoftwareFormAccess = useRoleAccess(allRoles);
	const hasDeviceFormAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const hasAdditionallyFormAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const hasWatchesFormAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const hasReportsFormAccess = useRoleAccess(allRoles);
	const hasBankSettingsFormAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const hasAndroidWatchesFormAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);

	useEffect(() => {
		if (fullInfoData.activeTab) {
			changeRestaurantFullInfoVisibleForm(fullInfoData.activeTab);
		} else if (hasMainFormAccess) {
			changeRestaurantFullInfoVisibleForm('mainForm');
		} else if (hasLocalizationFormAccess) {
			changeRestaurantFullInfoVisibleForm('localizationForm');
		} else if (hasStyleFormAccess) {
			changeRestaurantFullInfoVisibleForm('styleForm');
		} else if (hasSoftwareFormAccess) {
			changeRestaurantFullInfoVisibleForm('softwareForm');
		} else if (hasDeviceFormAccess) {
			changeRestaurantFullInfoVisibleForm('devicesForm');
		} else if (hasAdditionallyFormAccess) {
			changeRestaurantFullInfoVisibleForm('additionallyForm');
		} else if (hasWatchesFormAccess) {
			changeRestaurantFullInfoVisibleForm('watchesForm');
		} else if (hasReportsFormAccess) {
			changeRestaurantFullInfoVisibleForm('reportsForm');
		} else if (hasBankSettingsFormAccess) {
			changeRestaurantFullInfoVisibleForm('bankSettingsForm');
		} else if (hasAndroidWatchesFormAccess) {
			changeRestaurantFullInfoVisibleForm('androidWatchesForm');
		}
	}, [
		fullInfoData.activeTab,
		hasMainFormAccess,
		hasLocalizationFormAccess,
		hasStyleFormAccess,
		hasSoftwareFormAccess,
		hasDeviceFormAccess,
		hasAdditionallyFormAccess,
		hasWatchesFormAccess,
		hasReportsFormAccess,
		hasAndroidWatchesFormAccess,
	]);

	const getTab = () => {
		switch (restaurantFullInfoVisibleForm) {
			case 'mainForm':
				return hasMainFormAccess && <MainInfoTab />;
			case 'localizationForm':
				return hasLocalizationFormAccess && <LocalizationTab />;
			case 'styleForm':
				return hasStyleFormAccess && <StyleTab />;
			case 'softwareForm':
				return hasSoftwareFormAccess && <SoftwareTab />;
			case 'devicesForm':
				return hasDeviceFormAccess && <DevicesTab />;
			case 'additionallyForm':
				return hasAdditionallyFormAccess && <AdditionallyTab />;
			case 'watchesForm':
				return hasWatchesFormAccess && <WatchesTab />;
			case 'reportsForm':
				return hasReportsFormAccess && <ReportsTab />;
			case 'bankSettingsForm':
				return hasBankSettingsFormAccess && <BankSettingsTab />;
			case 'androidWatchesForm':
				return hasBankSettingsFormAccess && <AndroidWatchesTab />;
			default:
				return null;
		}
	};

	return <div data-test="Tabs">{getTab()}</div>;
};

export default Tabs;
